//---Simplified: just orchestrates queries + mutations using authApi.---//
import React, { createContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSession, refreshToken, getMe, login, logout } from "../api/authApi";
import { setAccessToken } from "./tokenStore";
import FullScreenLoading from "../components/common/FullScreenLoading";

type BranchInfo = {
  id: string | null;
  name: string | null;
};

type ClientInfo = {
  id: string | null;
  name: string | null;
  logo?: string | null;
};

type AuthData = {
  user: string | null;
  token: string | null;
  isStaff: boolean;
  isAdmin: boolean;
  isPower: boolean;
  isSuper: boolean;
  branch: BranchInfo | null;
  client: ClientInfo | null;
};

export const AuthContext = createContext<
  | {
      data: AuthData | null;
      isLoading: boolean;
      login: (
        u: string,
        p: string,
        o?: { onSuccess?: () => void; onError?: (e: any) => void },
      ) => void;
      logout: () => void;
    }
  | undefined
>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  const { data: queryData, isLoading } = useQuery<AuthData | null>({
    queryKey: ["auth"],
    queryFn: async () => {
      const session = await getSession();
      if (!session.data.active) return null;

      const access = await refreshToken();
      setAccessToken(access);

      const me = await getMe();
      return {
        user: me.data.message.replace("Hello ", ""),
        isStaff: !!me.data.is_staff,
        isAdmin: !!me.data.is_admin,
        isPower: !!me.data.is_power,
        isSuper: !!me.data.is_super,
        token: access,
        branch: me.data.branch
          ? {
              id: me.data.branch.id,
              name: me.data.branch.name,
            }
          : null,
        client: me.data.client
          ? {
              id: me.data.client.id,
              name: me.data.client.name,
              logo: me.data.client.logo || null,
            }
          : null,
      };
    },
    retry: false,
    refetchOnWindowFocus: false,
    meta: { onError: () => setAccessToken(null) },
  });

  const loginMutation = useMutation({
    mutationFn: (vars: { username: string; password: string }) =>
      login(vars.username, vars.password),
    onSuccess: (res) => {
      setAccessToken(res.access);
      queryClient.setQueryData<AuthData>(["auth"], {
        user: res.user,
        token: res.access,
        isStaff: res.is_staff,
        isAdmin: res.is_admin,
        isPower: res.is_power,
        isSuper: res.is_super,
        client: res.client || null,
        branch: res.branch || null,
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      setAccessToken(null);
      queryClient.setQueryData<AuthData | null>(["auth"], null);
    },
  });

  return (
    <AuthContext.Provider
      value={{
        data: queryData ?? null,
        isLoading,
        login: (u, p, o) =>
          loginMutation.mutate({ username: u, password: p }, o),
        logout: () => logoutMutation.mutate(),
      }}
    >
      {isLoading ? <FullScreenLoading /> : children}
    </AuthContext.Provider>
  );
};
